import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Leaf, Cpu, Users, Trophy, Lightbulb, Sparkles, Target, Award, ArrowRight } from 'lucide-react';

export const CategoriesPage = () => {
  const categories = [
    {
      title: 'Best Sustainable Stand',
      subtitle: 'Recognizes excellence in sustainable design across the entire lifecycle',
      icon: Leaf,
      description: 'This award recognizes excellence in sustainable design — projects that minimize environmental impact while maintaining high aesthetic and operational quality. Sustainability must be demonstrated across the entire lifecycle: design, sourcing, fabrication, transportation, operation, and reuse or recycling. The stand should serve as a benchmark for how environmental responsibility can elevate, not limit, creativity.',
      judgingFocus: [
        'Circular design: Reusability, modularity, or adaptability for future use or disposal.',
        'Material intelligence: Certified, recycled, or rapidly renewable resources.',
        'Operational efficiency: Low-energy lighting, optimized logistics, minimal waste.',
        'Durability and reusability: Modular components adaptable for future events.',
        'Quantified impact: Documented reductions in CO₂, waste, or shipping footprint.'
      ],
      submissionRequirements: 'Provide a sustainability report detailing material sourcing, construction methods, and reuse strategy. Include photographs of storage or reinstallation (if applicable) and any environmental certifications or third-party verifications.'
    },
    {
      title: 'Best Use of Technology',
      subtitle: 'Honors the most intelligent and purposeful use of technology to enhance storytelling',
      icon: Cpu,
      description: 'Honors the most intelligent and purposeful use of technology to enhance storytelling, interaction, and measurement. Whether through immersive environments (AR, VR, MR), responsive media, AI personalization, or data-driven installations, the winning entry must show how technology deepens, not distracts from, the brand experience. Technical sophistication should be matched by emotional resonance and clarity of purpose.',
      judgingFocus: [
        'Purpose alignment: Clear reasoning behind the technology\'s inclusion.',
        'User experience: Seamless integration between digital and physical touchpoints.',
        'Innovation quality: Use of emerging tools or novel interaction logic.',
        'Execution excellence: Stability, latency, and visual performance.',
        'Outcome measurement: Data capture, analytics, and conversion insights.'
      ],
      submissionRequirements: 'Provide a technical overview (architecture, hardware, software) and UX flow diagrams. Include demonstration videos (≤ 2 minutes) and post-event analytics showing engagement levels and system performance.'
    },
    {
      title: 'Best Visitor Experience',
      subtitle: 'Recognizes the stand that creates the most engaging and memorable visitor journey',
      icon: Users,
      description: 'This category recognizes the stand that creates the most engaging and memorable visitor journey. The experience should be designed intentionally — balancing attraction, exploration, interaction, and conversion. Every element, from spatial flow to human engagement, should invite discovery and foster emotional connection. A truly great visitor experience anticipates diverse needs, removes friction, and leaves a lasting impression.',
      judgingFocus: [
        'Journey design: Clarity of entry, exploration, and exit sequences.',
        'Interaction quality: Depth and relevance of engagement points.',
        'Inclusivity: Ease of movement, accessibility, and crowd management.',
        'Sensory orchestration: Use of sound, light, scent, and texture to guide emotion.',
        'Human element: Staff training, ambassador scripts, and consistency of service delivery.'
      ],
      submissionRequirements: 'Include a detailed experience-journey map or service blueprint, a floor plan highlighting visitor flow, and documentation (photos or videos) of each interaction zone. Quantitative metrics such as dwell time, conversion rates, or satisfaction scores strengthen the entry.'
    },
    {
      title: "Judge's Choice Award",
      subtitle: 'Reserved for the stand that demonstrates extraordinary mastery across all dimensions',
      icon: Trophy,
      description: 'Reserved for the stand that, in the collective judgment of the panel, demonstrates extraordinary mastery across all dimensions — creativity, design, engineering, and emotional impact. It may not score the highest in any single category, yet it embodies balance, originality, and excellence so complete that it defines the spirit of the competition. This is the jury\'s personal accolade to a stand that transcends conventional evaluation.',
      judgingFocus: [
        'Holistic brilliance: Seamless alignment of idea, craft, and outcome.',
        'Timeless quality: Design choices that feel enduring rather than trend-driven.',
        'Influence: Likely to inspire peers and future exhibitors.'
      ],
      submissionRequirements: 'Entrants do not apply directly; finalists from other categories become eligible. The jury selects one project that exemplifies cross-category excellence.'
    },
    {
      title: 'Best Brand Experience & Storytelling',
      subtitle: 'Rewards the stand that transforms a brand\'s essence into an immersive, coherent story',
      icon: Sparkles,
      description: 'Rewards the stand that transforms a brand\'s essence into an immersive, coherent story. Every visual, verbal, and spatial cue should reinforce a central narrative — one that communicates values, purpose, and emotion. The strongest entries use design as narrative architecture: guiding visitors through an experience that is both intellectually satisfying and emotionally memorable.',
      judgingFocus: [
        'Narrative clarity: A single, compelling story thread.',
        'Brand alignment: Authentic expression of values and positioning.',
        'Spatial storytelling: How architecture, graphics, and content work together.',
        'Emotional resonance: Depth of connection visitors feel to the brand.',
        'Consistency: Cohesion across all touchpoints and media.'
      ],
      submissionRequirements: 'Provide brand guidelines or a brand brief, a narrative concept statement, and a walkthrough (video or annotated images) showing how story unfolds spatially. Visitor testimonials or qualitative feedback strengthen the submission.'
    },
    {
      title: 'Most Innovative Stand Concept',
      subtitle: 'Celebrates breakthrough innovation that meaningfully redefines exhibition environments',
      icon: Lightbulb,
      description: 'Celebrates breakthrough innovation that meaningfully redefines how exhibition environments are imagined or constructed. Innovation may appear in materials, construction systems, interactive technology, storytelling methods, or even business processes. The concept should present a tangible improvement to sustainability, user experience, cost efficiency, or creative expression — not novelty for its own sake.',
      judgingFocus: [
        'Originality: Genuinely new approach or application.',
        'Impact: Measurable improvement in cost, sustainability, engagement, or build time.',
        'Scalability: Potential for wider adoption or adaptation.',
        'Execution: Successful implementation, not just concept.'
      ],
      submissionRequirements: 'Describe the innovation clearly (What\'s new? Why does it matter?). Provide technical documentation, prototypes, or case study data. If applicable, include client feedback, cost savings, or sustainability metrics that validate the innovation\'s value.'
    },
    {
      title: 'Best Structural Design & Engineering',
      subtitle: 'Celebrates precision engineering and intelligent construction',
      icon: Target,
      description: 'Celebrates precision engineering and intelligent construction. This category honors stands where structural innovation, material optimization, and fabrication excellence are visible and integral to the design. Whether through daring cantilevers, lightweight assemblies, modular systems, or complex geometries, the entry should demonstrate that engineering is not a constraint but a creative medium.',
      judgingFocus: [
        'Structural ambition: Complexity, scale, or daring in engineering.',
        'Material efficiency: Intelligent use of materials for strength and aesthetic.',
        'Fabrication quality: Precision in joints, finishes, and detailing.',
        'Innovation in assembly: Novel construction techniques or modular logic.',
        'Safety and compliance: Adherence to codes without compromising vision.'
      ],
      submissionRequirements: 'Include structural drawings, material specifications, and fabrication process documentation. Photographs should highlight connections, cantilevers, or other structural features. Engineer\'s statements or certifications add credibility.'
    },
    {
      title: 'Best Creative Concept',
      subtitle: 'Honors the stand concept that demonstrates outstanding originality and strategic imagination',
      icon: Award,
      description: 'Honors the stand concept that demonstrates outstanding originality and strategic imagination. This award is about the strength of the idea — the conceptual clarity and creative courage that set the project apart before a single material is sourced or a wall is built. The concept should be both surprising and inevitable: unexpected in execution, yet perfectly aligned with the brand, audience, and objectives.',
      judgingFocus: [
        'Conceptual clarity: A single, powerful idea that drives all decisions.',
        'Originality: Fresh perspective that avoids clichés.',
        'Strategic fit: Alignment with brand identity, audience needs, and event context.',
        'Executional integrity: Consistency between concept and realization.',
        'Memorability: Likely to be remembered and discussed long after the event.'
      ],
      submissionRequirements: 'Submit a concept statement (≤ 500 words) explaining the idea, its origins, and strategic rationale. Include mood boards, sketches, or early renderings that capture the conceptual vision, alongside final build photography to show how the idea was realized.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 mb-6">
            Award Categories
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
            8 prestigious categories recognizing excellence across all aspects of exhibition stand design
          </p>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-12 pb-0 bg-gray-50">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="categories-detail-grid">
            {categories.map((category, index) => (
              <Card
                key={index}
                className="p-6 sm:p-8 rounded-xl bg-white border shadow-sm hover:shadow-md transition-shadow"
                data-testid={`category-detail-${index}`}
              >
                {/* Category Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 h-14 w-14 rounded-xl bg-amber-500 flex items-center justify-center">
                    <category.icon className="h-7 w-7 text-white" aria-hidden="true" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                      {category.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {category.subtitle}
                    </p>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-6">
                  <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="h-1 w-1 bg-amber-500 rounded-full"></span>
                    Description
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {category.description}
                  </p>
                </div>

                {/* Judging Focus */}
                {category.judgingFocus && (
                  <div className="mb-6">
                    <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="h-1 w-1 bg-amber-500 rounded-full"></span>
                      Judging Focus
                    </h4>
                    <ul className="space-y-2">
                      {category.judgingFocus.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                          <span className="flex-shrink-0 mt-1.5 h-5 w-5 rounded bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700">
                            {idx + 1}
                          </span>
                          <span className="flex-1 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Submission Requirements */}
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                  <h4 className="text-sm font-bold text-amber-900 mb-2 flex items-center gap-2">
                    <span className="h-1 w-1 bg-amber-500 rounded-full"></span>
                    Submission Requirements
                  </h4>
                  <p className="text-sm text-amber-900 leading-relaxed">
                    {category.submissionRequirements}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Ready to Enter Section */}
      <section
        className="bg-gray-900 text-white py-20 sm:py-28"
        data-testid="ready-to-enter-section"
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white mb-6">
            Ready to Enter?
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto mb-8">
            Choose your category and submit your best exhibition stand design.
          </p>
          
          <Link to="/apply">
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-6 text-lg rounded-[10px] shadow-lg hover:shadow-xl transition-all duration-200 inline-flex items-center gap-2"
              data-testid="start-application-button"
            >
              Start Your Application
              <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>

          <p className="mt-8 text-sm text-gray-400">
            Deadline: June 10, 2026
          </p>
        </div>
      </section>
    </div>
  );
};